/**
 * Game plan wording — what every customer's game plan email says around their own steps and dates
 * (Heemo 2026-11-02: option A, edited in the app). Stored in Supabase `app_settings` (key 'game_plan'):
 * signed-in staff read it, only admins change it; n8n reads it with RPC app_setting when it sends a plan.
 * Per-job content (steps, dates, installer, visits) comes from the Job itself, not from here.
 */
import { getSupabase } from './auth';
import { CURRENT_USER } from './currentUser';

const KEY = 'game_plan';

// draft wording — facts only (depth, R-value, what happens), no savings %, rebate always "up to"
export const DEFAULT_GAME_PLAN = {
  intro: 'Thank you for choosing Canadian Insulation Experts. Here is your game plan — what we are doing, when, and how to get ready.',
  steps: {
    deposit: { about: 'Your deposit holds your install day.', timing: 'Due before your install day.' },
    pre_audit: { about: 'An Enbridge energy advisor visits to record your home before the upgrade. It is required for the rebate and takes about 1–2 hours. Someone 18+ needs to be home.', timing: 'Before your install day.' },
    install: { about: 'Our crew does the work in your quote.', timing: 'The day we book with you.' },
    post_audit: { about: 'The energy advisor comes back to confirm the upgrade for your rebate.', timing: 'Usually 1–3 weeks after your install.' },
    report: { about: 'We send you before & after photos of your attic and the depth we installed.', timing: 'Within 2 business days of your install.' },
    final_payment: { about: 'The remaining balance — by card with your invoice link, e-transfer or cheque.', timing: 'Due when the work is complete.' },
    removal: { about: 'Our crew removes the old insulation and cleans out the attic, ready for the new insulation.', timing: 'The day we book with you, before your install day.' },
    roofer: { about: 'A roofer works on the roof side of the job (vents or roof repairs) so the attic can breathe.', timing: 'The day we book with you.' },
    carpenter: { about: 'A carpenter does the woodwork the job needs, such as the attic hatch or framing.', timing: 'The day we book with you.' },
    return_visit: { about: 'We come back to finish or touch up part of the work.', timing: 'The day we book with you.' },
    progress_payment: { about: 'A part payment partway through the job.', timing: 'Due on the date we agree with you.' },
    rebate_paperwork: { about: 'We send the paperwork for your rebate.', timing: 'After the Enbridge post-inspection.' },
  },
  installLength: {
    default: 'Most installs take 4–6 hours.',
    // first match wins: a word or phrase in the package name → its install length
    byPackage: [{ match: 'Clean-Out', text: 'Removing the old insulation and blowing in new usually takes a full day.' }],
  },
  before: [
    'Clear a path to the attic hatch and move anything stored under it.',
    'If the hatch is inside a closet, please empty the closet.',
    'Leave room in the driveway (or in front of the house) for our truck.',
    'Keep pets in a separate room while we work.',
    'An adult (18+) should be home when we arrive and when we finish.',
  ],
  onTheDay: 'We arrive in your arrival window, walk you through the plan, and cover the path to the attic. The insulation hose comes in through a door or window and the machine runs outside, so expect some noise. You are welcome to stay home. Before we leave we clean up and show you the finished attic.',
  after: 'We send your before & after photo report and the balance owing. All work is covered by our 10-Year Workmanship Warranty.',
};

// saved settings on top of the defaults, so a new field added later always has wording
const merge = (saved = {}) => ({
  ...DEFAULT_GAME_PLAN,
  ...saved,
  steps: { ...DEFAULT_GAME_PLAN.steps, ...(saved.steps || {}) },
  installLength: { ...DEFAULT_GAME_PLAN.installLength, ...(saved.installLength || {}) },
});

export const loadGamePlan = async () => {
  const sb = getSupabase();
  if (!sb) return { plan: merge(), updatedAt: null, updatedBy: null };
  const { data, error } = await sb.from('app_settings').select('value, updated_at, updated_by').eq('key', KEY).maybeSingle();
  if (error) throw new Error('Couldn’t load the game plan settings');
  return { plan: merge(data ? data.value : {}), updatedAt: data ? data.updated_at : null, updatedBy: data ? data.updated_by : null };
};

export const saveGamePlan = async (plan) => {
  const sb = getSupabase();
  if (!sb) throw new Error('Saving needs the live app');
  const { error } = await sb.from('app_settings').upsert({ key: KEY, value: plan, updated_at: new Date().toISOString(), updated_by: CURRENT_USER.name || 'Admin' });
  if (error) throw new Error(error.code === '42501' ? 'Only an admin can change the game plan' : 'Couldn’t save — try again');
  return 'Game plan saved';
};

// the install length line for a package name (first byPackage match, else the default)
export const installLengthFor = (plan, packageName = '') => {
  const hit = (plan.installLength.byPackage || []).find((r) => r.match && String(packageName).toLowerCase().includes(String(r.match).toLowerCase()));
  return hit ? hit.text : plan.installLength.default;
};
